import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { createPortal } from 'react-dom';

const CartAnimationContext = createContext(null);

let nextId = 0;

/* ── Pixie dust sparkle ─────────────────────────────────────────────────── */

const SPARKLE_COLORS = ['#f9d451', '#d272ff', '#ffffff', '#ffb3f7', '#ffe066'];

const PixieDust = ({ angle }) => {
  // Generate sparkles spread in the pixie's wake (behind the direction of travel)
  const sparkles = useMemo(() => {
    return Array.from({ length: 10 }, (_, i) => {
      // Spread behind the direction of travel with some spread angle
      const backAngle = angle + Math.PI + (Math.random() - 0.5) * 1.4;
      const dist = 22 + Math.random() * 40;
      return {
        id: i,
        sx: Math.cos(backAngle) * dist,
        sy: Math.sin(backAngle) * dist,
        color: SPARKLE_COLORS[i % SPARKLE_COLORS.length],
        size: 7 + Math.random() * 7,
        delay: i * 140,
        isStar: i % 2 === 0,
      };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {sparkles.map((s) => (
        <div
          key={s.id}
          className={`cart-fly-sparkle${s.isStar ? ' cart-fly-sparkle--star' : ''}`}
          style={{
            width: s.size,
            height: s.size,
            background: s.isStar ? 'none' : s.color,
            color: s.color,       // for star clip-path using currentColor hack
            '--sx': `${s.sx}px`,
            '--sy': `${s.sy}px`,
            animationDelay: `${s.delay}ms`,
          }}
        />
      ))}
    </>
  );
};

/* ── Flying pixie ───────────────────────────────────────────────────────── */

const FlyingPixie = ({ id, sourceRect, targetRect, onRemove }) => {
  const size = 60;    // overall container size (drives positioning)
  const imgSize = 52; // pixie image size
  const half = size / 2;

  const startX = sourceRect.left + sourceRect.width / 2 - half;
  const startY = sourceRect.top + sourceRect.height / 2 - half;
  const endX   = targetRect.left + targetRect.width  / 2 - half;
  const endY   = targetRect.top  + targetRect.height / 2 - half;

  // Direction: flip pixie horizontally if flying right-to-left
  const goingLeft = endX < startX;
  // Tilt: slight lean in direction of travel
  const tiltDeg = goingLeft ? -18 : 18;

  // Pre-compute travel angle for dust trail direction
  const angle = Math.atan2(endY - startY, endX - startX);

  useEffect(() => {
    const t = setTimeout(() => onRemove(id), 2000);
    return () => clearTimeout(t);
  }, [id, onRemove]);

  return (
    <div
      className="cart-fly-outer"
      style={{
        position: 'fixed',
        left: startX,
        top: startY,
        width: size,
        height: size,
        pointerEvents: 'none',
        zIndex: 99999,
        '--dx': `${endX - startX}px`,
        '--dy': `${endY - startY}px`,
        '--pxsize': `${size}px`,
        '--imgsize': `${imgSize}px`,
      }}
    >
      <div className="cart-fly-inner">
        {/* Flip + tilt wrapper — no animation here so transform is stable */}
        <div
          className="cart-fly-flip"
          style={{ transform: `scaleX(${goingLeft ? -1 : 1}) rotate(${tiltDeg}deg)` }}
        >
          {/* Fade-out container */}
          <div className="cart-fly-pixie-container">
            {/* Glow bubble */}
            <div className="cart-fly-pixie-bubble" />

            <img
              src="/assets/images/pixie.png"
              className="cart-fly-pixie"
              alt=""
              draggable={false}
            />

            {/* Pixie dust trail */}
            <PixieDust angle={goingLeft ? Math.PI - angle : angle} />
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Portal overlay ─────────────────────────────────────────────────────── */

const CartAnimationOverlay = ({ particles, onRemove }) =>
  createPortal(
    <>
      {/* Hidden SVG filter definitions */}
      <svg
        style={{ position: 'fixed', width: 0, height: 0, overflow: 'hidden' }}
        aria-hidden="true"
      >
        <defs>
          {/* Gooey merge filter for the cart icon */}
          <filter id="cart-goo-filter" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="5" result="blur" />
            <feColorMatrix
              in="blur"
              mode="matrix"
              values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 18 -8"
              result="goo"
            />
            <feComposite in="SourceGraphic" in2="goo" operator="atop" />
          </filter>

        </defs>
      </svg>

      {particles.map((p) => (
        <FlyingPixie key={p.id} {...p} onRemove={onRemove} />
      ))}
    </>,
    document.body,
  );

/* ── Provider ───────────────────────────────────────────────────────────── */

export const CartAnimationProvider = ({ children }) => {
  const cartIconRef = useRef(null);
  const [particles, setParticles] = useState([]);

  const triggerCartAnimation = useCallback((sourceElement) => {
    const sourceRect =
      typeof sourceElement?.getBoundingClientRect === 'function'
        ? sourceElement.getBoundingClientRect()
        : sourceElement;
    const targetRect = cartIconRef.current?.getBoundingClientRect();
    if (!sourceRect || !targetRect) return;

    setParticles((prev) => [
      ...prev,
      { id: ++nextId, sourceRect, targetRect },
    ]);
  }, []);

  const removeParticle = useCallback((id) => {
    setParticles((prev) => prev.filter((p) => p.id !== id));
  }, []);

  return (
    <CartAnimationContext.Provider value={{ cartIconRef, triggerCartAnimation }}>
      {children}
      <CartAnimationOverlay particles={particles} onRemove={removeParticle} />
    </CartAnimationContext.Provider>
  );
};

export const useCartAnimation = () => useContext(CartAnimationContext);
